import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Inbox as InboxIcon, MailOpen, Reply, ShieldOff, Sparkles, Loader2, Send, X, RefreshCw, Clock, Eye, Building2, AlertTriangle } from "lucide-react";

const Stat = ({ label, value, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-[#121824] p-4 kz-fade-up">
    <div className={`kz-head text-2xl font-extrabold ${accent}`}>{value}</div>
    <div className="text-[11px] text-slate-400 mt-0.5 font-medium">{label}</div>
  </div>
);

const cleanSnippet = (s) => (s || "").replace(/[\u200b-\u200d\uFEFF\u034f]/g, "").replace(/\s+/g, " ").trim();

export const Inbox = () => {
  const [data, setData] = useState(null);
  const [nudges, setNudges] = useState(null);
  const [loading, setLoading] = useState(false);
  const [composer, setComposer] = useState(null); // {email, subject, company, opp_id, draft, source}

  const load = async () => {
    setLoading(true);
    try {
      const [box, nd] = await Promise.all([api.inbox(), api.inboxNudges()]);
      setData(box); setNudges(nd);
    } catch { toast.error("Couldn't load the inbox."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const draft = async ({ sender, subject, snippet, email, company, opp_id, source }) => {
    setComposer({ email, subject: subject || "", company, opp_id, draft: "", loading: true, source });
    try {
      const r = await api.draftFollowup({ sender: sender || email, subject, snippet, email });
      setComposer((c) => c ? { ...c, draft: r.draft, loading: false } : c);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't draft a follow-up.";
      toast.error(msg);
      setComposer(null);
    }
  };

  const send = async () => {
    if (!composer?.draft?.trim()) return;
    setComposer((c) => ({ ...c, sending: true }));
    try {
      await api.sendFollowup({ email: composer.email, subject: composer.subject, body: composer.draft, opp_id: composer.opp_id, company: composer.company });
      toast.success(`Follow-up sent to ${composer.company || composer.email}.`);
      setComposer(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
      setComposer((c) => c ? { ...c, sending: false } : c);
    }
  };

  const c = data?.counts || {};
  const nc = nudges || {};

  return (
    <div data-testid="screen-inbox" className="max-w-6xl mx-auto">
      <div className="kz-fade-up flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-emerald-400 font-semibold tracking-wide">CONVERSATIONS</p>
          <h1 className="kz-head text-3xl md:text-4xl font-extrabold tracking-tight mt-2">Replies & follow-ups.</h1>
          <p className="text-slate-400 mt-2 text-sm">Two-way conversations, matched to your prospects. Every follow-up waits for your one-tap approval.</p>
        </div>
        <button data-testid="inbox-refresh-btn" onClick={load} disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.06] text-slate-100 font-semibold text-sm hover:bg-white/[0.1] disabled:opacity-60 transition-all">
          {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} className="text-emerald-400" />}
          {loading ? "Syncing…" : "Refresh"}
        </button>
      </div>

      {data && !data.live && (
        <div className="mt-6 rounded-2xl border border-amber-500/20 bg-amber-500/[0.06] p-4 flex items-center gap-3 text-sm text-amber-200">
          <AlertTriangle size={16} /> Mailbox not connected yet{data.error ? ` — ${data.error}` : ""}. Replies will appear here once IMAP is live.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mt-8">
        <Stat label="Replies" value={c.total ?? 0} accent="text-blue-400" />
        <Stat label="Actionable" value={c.actionable ?? 0} accent="text-emerald-400" />
        <Stat label="Opted out" value={c.opted_out ?? 0} accent="text-red-400" />
        <Stat label="Opened, no reply" value={nc.opened ?? 0} accent="text-amber-400" />
      </div>

      {/* Follow-up nudges */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-4"><MailOpen size={18} className="text-amber-400" /><h2 className="kz-head text-lg font-bold">Follow-up nudges</h2>
          <span className="text-[11px] text-slate-500">KURT drafts, you approve</span></div>
        {!nudges ? (
          <div className="h-24 animate-pulse bg-white/5 rounded-2xl" />
        ) : nudges.nudges.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-[#121824] p-6 text-center text-sm text-slate-400">No nudges yet. When a prospect opens an email or goes quiet, KURT flags a follow-up here.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {nudges.nudges.map((n) => (
              <div key={n.id} data-testid={`nudge-${n.id}`} className="rounded-2xl border border-white/10 bg-[#121824] p-4 kz-fade-up">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 size={14} className="text-slate-500 shrink-0" />
                    <span className="text-sm font-semibold text-slate-100 truncate">{n.company || n.email}</span>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${n.opened ? "bg-amber-500/15 text-amber-300" : "bg-white/[0.06] text-slate-400"}`}>{n.reason}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1.5 truncate">{n.subject || "(no subject)"}</p>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                  {n.opened && <span className="flex items-center gap-1 text-amber-300"><Eye size={12} /> {n.open_count}×</span>}
                  <span className="flex items-center gap-1"><Clock size={12} /> {n.age_days}d ago</span>
                </div>
                <button data-testid={`nudge-draft-${n.id}`} onClick={() => draft({ sender: n.email, subject: n.subject, snippet: n.body, email: n.email, company: n.company, opp_id: n.opp_id, source: "nudge" })}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-white/[0.06] text-slate-100 font-semibold text-xs hover:bg-white/[0.1] transition-all">
                  <Sparkles size={13} className="text-emerald-400" /> Draft follow-up
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Replies */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-4"><InboxIcon size={18} className="text-emerald-400" /><h2 className="kz-head text-lg font-bold">Replies</h2></div>
        {!data ? (
          <div className="h-40 animate-pulse bg-white/5 rounded-2xl" />
        ) : data.replies.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-[#121824] p-8 text-center text-sm text-slate-400">No replies in the inbox yet.</div>
        ) : (
          <div className="space-y-3">
            {data.replies.map((r) => (
              <div key={r.id} data-testid={`reply-${r.id}`} className="rounded-2xl border border-white/10 bg-[#121824] p-4 kz-fade-up">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-100">{r.from}</span>
                      {r.company && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 flex items-center gap-1"><Building2 size={10} /> {r.company}</span>}
                      {r.opted_out && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 flex items-center gap-1"><ShieldOff size={10} /> Opted out</span>}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{r.subject || "(no subject)"} · <span className="text-slate-600">{r.date}</span></p>
                  </div>
                  {r.can_followup ? (
                    <button data-testid={`reply-draft-${r.id}`} onClick={() => draft({ sender: r.from, subject: r.subject, snippet: r.body, email: r.from_email, company: r.company, opp_id: r.opp_id, source: "reply" })}
                      className="shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500 text-[#04140d] font-semibold text-xs hover:bg-emerald-400 transition-all">
                      <Reply size={13} /> Draft reply
                    </button>
                  ) : r.suppressed ? (
                    <span className="shrink-0 text-[11px] text-red-300/80 flex items-center gap-1"><ShieldOff size={12} /> Suppressed — no follow-up</span>
                  ) : null}
                </div>
                <p className="text-sm text-slate-300 mt-2.5 leading-relaxed whitespace-pre-wrap">{cleanSnippet(r.snippet)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Composer modal */}
      {composer && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => !composer.sending && setComposer(null)}>
          <div data-testid="followup-composer" className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#121824] p-6 kz-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="kz-head text-lg font-bold flex items-center gap-2"><Sparkles size={16} className="text-emerald-400" /> KURT's follow-up</h3>
              <button data-testid="composer-close" onClick={() => !composer.sending && setComposer(null)} className="text-slate-500 hover:text-slate-200"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-400 mb-3">To {composer.company || composer.email} · held for your approval</p>
            {composer.loading ? (
              <div className="h-40 flex items-center justify-center text-slate-500 text-sm gap-2"><Loader2 size={16} className="animate-spin" /> KURT is writing…</div>
            ) : (
              <textarea data-testid="composer-textarea" value={composer.draft} onChange={(e) => setComposer((cc) => ({ ...cc, draft: e.target.value }))}
                rows={9} className="w-full bg-[#0B0F17] border border-white/10 rounded-xl p-3 text-sm text-slate-200 outline-none focus:border-emerald-500/40 resize-none leading-relaxed" />
            )}
            <div className="flex items-center justify-end gap-2 mt-4">
              <button data-testid="composer-cancel" onClick={() => setComposer(null)} disabled={composer.sending}
                className="px-4 py-2 rounded-full text-sm font-semibold text-slate-300 hover:bg-white/[0.06] disabled:opacity-60">Cancel</button>
              <button data-testid="composer-send" onClick={send} disabled={composer.loading || composer.sending || !composer.draft?.trim()}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-[#04140d] font-semibold text-sm hover:bg-emerald-400 disabled:opacity-50 transition-all">
                {composer.sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {composer.sending ? "Sending…" : "Approve & send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
